/**
 * DiffCard — inline rendering of a tracked edit inside the chat stream.
 *
 * Shows:
 *   - File path (repo-relative when possible)
 *   - Kind badge (Edit / Write / MultiEdit)
 *   - Stats (+N -M)
 *   - Unified diff rendered with diff2html (line-by-line)
 *   - Revert button (disabled once reverted or for non-revertable kinds)
 *
 * Collapsible by default so long diffs don't drown the chat. Click the
 * header to expand.
 */

import { useState, useEffect, useRef } from "react";
import {
  FileText,
  FilePlus,
  FileCode2,
  ChevronRight,
  ChevronDown,
  RotateCcw,
  Check,
  AlertTriangle,
} from "lucide-react";
import { html as diff2htmlParse } from "diff2html";
import "diff2html/bundles/css/diff2html.min.css";
import type { SerializedEdit } from "@black-wall/shared";

/** The wire shape of a tracked edit (see the API's `serializeEdit`). */
export type TrackedEditView = SerializedEdit;

interface DiffCardProps {
  sessionId: string;
  edit: TrackedEditView;
  onReverted?: (editId: string) => void;
  defaultOpen?: boolean;
}

export function DiffCard({
  sessionId,
  edit,
  onReverted,
  defaultOpen = false,
}: DiffCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [reverting, setReverting] = useState(false);
  const [revertError, setRevertError] = useState<string | null>(null);
  const [localReverted, setLocalReverted] = useState(edit.reverted);
  const diffRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !diffRef.current) return;
    try {
      const rendered = diff2htmlParse(edit.unifiedDiff, {
        drawFileList: false,
        matching: "lines",
        outputFormat: "line-by-line",
        diffStyle: "word",
      });
      diffRef.current.innerHTML = rendered;
    } catch (err) {
      diffRef.current.innerHTML = `<pre class="text-xs whitespace-pre-wrap">${escapeHtml(
        edit.unifiedDiff,
      )}</pre>`;
      console.warn("diff2html render failed:", err);
    }
  }, [open, edit.unifiedDiff]);

  const handleRevert = async () => {
    if (reverting || localReverted) return;
    setReverting(true);
    setRevertError(null);
    try {
      const res = await fetch(
        `/api/chat/sessions/${sessionId}/edits/${edit.id}/revert`,
        { method: "POST" },
      );
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setRevertError(data.error ?? `HTTP ${res.status}`);
        return;
      }
      setLocalReverted(true);
      onReverted?.(edit.id);
    } catch (err) {
      setRevertError(err instanceof Error ? err.message : "Network error");
    } finally {
      setReverting(false);
    }
  };

  const kindLabel =
    edit.kind === "edit"
      ? "EDIT"
      : edit.kind === "write"
        ? "WRITE"
        : edit.kind === "multi_edit"
          ? "MULTIEDIT"
          : "NOTEBOOK";

  const KindIcon =
    edit.kind === "write"
      ? FilePlus
      : edit.kind === "notebook_edit"
        ? FileCode2
        : FileText;

  return (
    <div className="my-2 border border-[var(--bw-border)] bg-[var(--bw-bg-alt)] font-mono text-xs">
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[var(--bw-bg)] transition-colors"
      >
        {open ? (
          <ChevronDown size={12} className="text-[var(--bw-muted)] flex-shrink-0" />
        ) : (
          <ChevronRight size={12} className="text-[var(--bw-muted)] flex-shrink-0" />
        )}
        <KindIcon size={12} className="text-[var(--bw-accent)] flex-shrink-0" />
        <span className="text-[10px] uppercase tracking-wider text-[var(--bw-accent)] flex-shrink-0">
          {kindLabel}
        </span>
        <span className="text-[var(--bw-fg)] truncate flex-1 min-w-0">
          {edit.displayPath}
        </span>
        <span className="text-[10px] flex-shrink-0">
          <span className="text-[var(--bw-ok,#7fe)]">+{edit.additions}</span>{" "}
          <span className="text-[var(--bw-warn,#f77)]">-{edit.deletions}</span>
        </span>
        {localReverted && (
          <span className="text-[10px] uppercase text-[var(--bw-muted)] flex-shrink-0">
            reverted
          </span>
        )}
      </button>

      {/* Body */}
      {open && (
        <div className="border-t border-[var(--bw-border)]">
          <div
            ref={diffRef}
            className="diff-wrapper overflow-x-auto text-[11px]"
          />
          <div className="flex items-center gap-2 px-3 py-2 border-t border-[var(--bw-border)]">
            {edit.revertable && !localReverted && (
              <button
                type="button"
                onClick={handleRevert}
                disabled={reverting}
                className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[var(--bw-border)] hover:border-[var(--bw-accent)] hover:text-[var(--bw-accent)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RotateCcw size={10} />
                {reverting ? "Reverting…" : "Revert"}
              </button>
            )}
            {localReverted && (
              <span className="flex items-center gap-1 text-[10px] text-[var(--bw-muted)]">
                <Check size={10} />
                Reverted to previous state
              </span>
            )}
            {!edit.revertable && (
              <span className="text-[10px] text-[var(--bw-muted)]">
                (not revertable)
              </span>
            )}
            {revertError && (
              <span className="flex items-center gap-1 text-[10px] text-[var(--bw-warn,#f77)]">
                <AlertTriangle size={10} />
                {revertError}
              </span>
            )}
            <span className="ml-auto text-[10px] text-[var(--bw-muted)]">
              {new Date(edit.createdAt).toLocaleTimeString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
